/**
 * 内衣 Band/Cup 尺码换算器（逻辑与形态对齐线上老站 www.fanka.com）。
 *
 * 一行三个下拉：Band + Cup = Size。原生尺码按钮由 CSS 隐藏，但**没有被删除** ——
 * 按钮里的 radio 仍是真正的表单状态，本组件靠勾选它们 + 派发冒泡 change 来驱动
 * Horizon <variant-picker>（variant-picker.js:48 的容器级监听）完成换图/改价/更新 URL。
 *
 * 状态机（照搬老项目 assets/bra-size-mapper.js 的 onBandCupChange）：
 * - Band/Cup 都空     → 恢复全部 Size 可选，清空 Size
 * - 只选了一个        → 按该维度算出「可达尺码集合」，其余 option 置灰；清空 Size 等用户补齐
 * - 两个都选、查得到  → Size 回填 + 勾 radio + 显示反向提示「Size 6 (M) fits 36B, 38A…」
 * - 两个都选、查不到  → 整个 Size 下拉隐藏、原位显示「无对应尺码」框 + 红色 alert 文案
 * - 直接改 Size       → 清空 Band/Cup（一个尺码对应多个组合，无法反向唯一确定）+ 勾 radio
 *
 * ⚠️ 四个与 Horizon 打交道的坑，都是实测踩出来的，改动前务必理解：
 *
 * 1. 我们的 select 事件必须就地拦停（#handleOwnChange 里 stopPropagation）。
 *    三个 select 都在 <variant-picker> 内部，change 冒泡上去会被 Horizon 的 variantChanged
 *    当成「变体下拉被改了」处理，它会给我们的 option 补 selected 属性，进而污染下面第 2 条。
 *
 * 2. Size 下拉的 option 永不带 selected 属性（Liquid 侧也不渲染，原因见 snippet 注释）。
 *    Horizon 的 selectedOptionsValues 用全局选择器 `select option[selected]` 扫描，
 *    一个陈旧的 selected 会和 radio 的新值一起被读到，服务端按旧值渲染再 morph 回来，
 *    表现为「选了没反应，但 URL 变了」。
 *
 * 3. morph 自己也会给我们的 option 盖 selected 属性（它对表单元素有保留状态的特殊处理），
 *    所以还得在 morph 后主动剥掉（#stripSelectedAttributes）。这条和第 1 条是**两条独立的
 *    污染路径**：stopPropagation 拦事件，拦不住 morph 写 DOM。只堵一条的症状是
 *    「第一次切换正常，之后点什么都没反应」。
 *
 * 4. morph 会用服务端 HTML 重写本组件内部，Band/Cup/Size 的选择与 option 的置灰状态都会丢，
 *    所以 MutationObserver 里要把这些客户端状态整套恢复（#restoreAfterMorph）。
 *
 * ⚠️ 隐藏原生按钮意味着本组件是唯一的尺码入口，坏掉就没法下单。所以映射表解析失败时
 * 组件**自我移除**（this.remove()）—— CSS 隐藏按钮用的是 `.fanka-bra-size-mapper ~ ...`
 * 兄弟选择器，组件一消失选择器立刻失配，原生按钮自动复原。
 *
 * 基类用裸 HTMLElement（参照 product-list-tabs.js），不用 Horizon 的 Component。
 */
class BraSizeMapper extends HTMLElement {
  #map = null;
  #fitLabels = {};
  #isSyncing = false;
  #observer = null;
  /**
   * 用户最近一次选的 Band/Cup。Horizon 每次变体切换都会服务端重渲染 + morph，
   * 而服务端不知道 Band/Cup（纯客户端状态），morph 后这两个框会被清空 ——
   * 用户刚选完就看到自己的选择消失。这里记一份，morph 后恢复回去。
   */
  #lastCombo = { band: '', cup: '' };

  connectedCallback() {
    this.#map = this.#readMap();

    // 映射表不可用 → 自我移除，让原生尺码按钮复原，保住下单路径
    if (!this.#map) {
      this.remove();
      return;
    }

    this.#fitLabels = this.#buildFitLabels(this.#map);

    // 事件委托挂在自身：morph 会用服务端 HTML 重写本组件内部，
    // 直接绑在 select 元素上的监听会随元素被替换而丢失。
    this.addEventListener('change', this.#handleOwnChange);
    this.#sizeFieldset?.addEventListener('change', this.#handleRadioChange);

    this.#stripSelectedAttributes();
    this.#syncSizeFromRadio();
    this.#updateSizeOptionStates('', '');
    this.#updatePendingState();

    // morph 之后把被冲掉的客户端状态整套恢复。只在「有 select/option 节点被增删」或
    // 「selected 属性被改动」时才处理 —— 否则提示文案的 textContent 变化也会触发。
    this.#observer = new MutationObserver((records) => {
      const relevant = records.some((record) => {
        if (record.type === 'attributes') return true;
        return [...record.addedNodes, ...record.removedNodes].some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.('select, option') || node.querySelector?.('select, option'))
        );
      });
      if (relevant) this.#restoreAfterMorph();
    });
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['selected'],
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleOwnChange);
    this.#sizeFieldset?.removeEventListener('change', this.#handleRadioChange);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #bandSelect() {
    return this.querySelector('[data-bra-band]');
  }

  get #cupSelect() {
    return this.querySelector('[data-bra-cup]');
  }

  get #sizeSelect() {
    return this.querySelector('[data-bra-size]');
  }

  get #sizeWrapper() {
    return this.querySelector('[data-bra-size-wrapper]');
  }

  get #sizeUnavailableEl() {
    return this.querySelector('[data-bra-size-unavailable]');
  }

  get #errorEl() {
    return this.querySelector('[data-bra-error]');
  }

  get #hintEl() {
    return this.querySelector('[data-bra-hint]');
  }

  /** 本组件就渲染在尺码 fieldset 内部（legend 之后），直接取最近祖先即可 */
  get #sizeFieldset() {
    return this.closest('fieldset');
  }

  get #sizeRadios() {
    return Array.from(this.#sizeFieldset?.querySelectorAll('input[type="radio"]') ?? []);
  }

  /** Size 下拉里的真实尺码 option（排除空占位） */
  get #sizeOptions() {
    return Array.from(this.#sizeSelect?.options ?? []).filter((option) => option.value);
  }

  /**
   * 读内联的映射表 JSON：
   * - 返回 map 对象，形如 { "B": { "36": "6/M" } }
   * - 解析失败或不存在时返回 null，调用方据此自我移除
   */
  #readMap() {
    const script = this.querySelector('[data-bra-map]');
    if (!script) return null;
    try {
      const parsed = JSON.parse(script.textContent);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * 把映射表反向索引成「尺码标签 → 能落到它的所有 Band+Cup 组合」：
   * - map：正向映射表，map[cup][band] = 尺码标签
   * 返回如 { "4/S": ["34A", "34B", "36A"] }，用于 #showHint 的反向提示。
   * 组合按 band 数值升序、同 band 按字母序（与老项目一致，否则提示里顺序random）。
   */
  #buildFitLabels(map) {
    const fitLabels = {};

    Object.keys(map).forEach((cup) => {
      Object.keys(map[cup] ?? {}).forEach((band) => {
        const label = map[cup][band];
        if (!label) return;
        fitLabels[label] = fitLabels[label] ?? [];
        fitLabels[label].push(`${band}${cup}`);
      });
    });

    Object.keys(fitLabels).forEach((label) => {
      fitLabels[label].sort((a, b) => {
        const bandA = Number.parseInt(a, 10);
        const bandB = Number.parseInt(b, 10);
        if (bandA !== bandB) return bandA - bandB;
        return a.localeCompare(b);
      });
    });

    // 再补一份以「商品真实尺码」为 key 的别名，因为提示是按 radio 的值去查的
    // （映射表标签 '4/S' 与后台 variant '4 (S)' 格式不同，见 #normalize 注释）
    Object.keys({ ...fitLabels }).forEach((label) => {
      const resolved = this.#findRadio(label)?.value;
      if (resolved) fitLabels[resolved] = fitLabels[label];
    });

    return fitLabels;
  }

  /**
   * 归一化尺码字符串，吸收映射表与后台 variant 命名的格式差异：
   * - value：如 '4/S'（映射表写法）、'4 (S)'（后台 variant 写法），统一得到 '4s'
   * 映射表由开发维护、variant 名称由运营在后台填，两边必然漂移，这层容错是刚需 ——
   * 实测线上两者格式确实不同（斜杠 vs 圆括号），去掉归一化整个换算器立刻静默失效。
   */
  #normalize(value) {
    return String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  /**
   * 把映射表给出的尺码标签匹配到真实 radio：
   * - label：映射表查出的标签，如 '6/M'
   * 先试严格全等（防将来 variant 改名后格式恰好一致），再退到归一化比对。
   */
  #findRadio(label) {
    const raw = String(label ?? '').trim();
    if (!raw) return null;

    const exact = this.#sizeRadios.find((radio) => radio.value.trim() === raw);
    if (exact) return exact;

    const target = this.#normalize(raw);
    if (!target) return null;
    return this.#sizeRadios.find((radio) => this.#normalize(radio.value) === target) ?? null;
  }

  /**
   * 查表：
   * - band：下胸围，如 36（映射表内层 key 是字符串，需转换）
   * - cup：杯型，如 'B'
   * 查不到返回 null —— 映射表是稀疏的，这是正常路径不是异常。
   */
  #lookup(band, cup) {
    if (!band || !cup) return null;
    return this.#map?.[cup]?.[String(band)] ?? null;
  }

  /**
   * 算出当前选择下「可达的尺码值集合」（返回的是 radio/option 的真实 value）：
   * - band：已选下胸围，未选时空字符串
   * - cup：已选杯型，未选时空字符串
   * 四个分支与老项目 getEligibleSizes 一致：
   * 都空 → 全部；都有 → 唯一那个；只有 band → 遍历所有 cup；只有 cup → 遍历该 cup 下所有 band。
   */
  #getEligibleSizes(band, cup) {
    const eligible = new Set();
    const map = this.#map ?? {};

    if (!band && !cup) {
      this.#sizeOptions.forEach((option) => eligible.add(option.value));
      return eligible;
    }

    if (band && cup) {
      const radio = this.#findRadio(this.#lookup(band, cup));
      if (radio) eligible.add(radio.value);
      return eligible;
    }

    if (band) {
      Object.keys(map).forEach((cupKey) => {
        const radio = this.#findRadio(map[cupKey]?.[String(band)]);
        if (radio) eligible.add(radio.value);
      });
      return eligible;
    }

    Object.keys(map[cup] ?? {}).forEach((bandKey) => {
      const radio = this.#findRadio(map[cup][bandKey]);
      if (radio) eligible.add(radio.value);
    });
    return eligible;
  }

  /**
   * 按当前 Band/Cup 联动置灰 Size 的 option：
   * - band / cup：当前选择，未选时空字符串
   * 售罄的 option 由 Liquid 打了 data-sold-out，这里要 `售罄 || 不可达` 一起算，
   * 否则会把服务端标记为售罄的 option 重新启用。
   * 若当前选中值变得不可选，连带清空 Size 显示（与老项目一致）。
   */
  #updateSizeOptionStates(band, cup) {
    const select = this.#sizeSelect;
    if (!select) return;

    const eligible = this.#getEligibleSizes(band, cup);

    this.#sizeOptions.forEach((option) => {
      const soldOut = option.hasAttribute('data-sold-out');
      option.disabled = soldOut || !eligible.has(option.value);
    });

    const current = select.value;
    if (!current) return;
    const currentOption = this.#sizeOptions.find((option) => option.value === current);
    if (!currentOption || currentOption.disabled) select.value = '';
  }

  /**
   * 本组件内三个 select 的统一入口：
   * - event：委托到组件自身的 change 事件
   * stopPropagation 是必须的，见文件头第 1 条。
   */
  #handleOwnChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;

    event.stopPropagation();
    if (this.#isSyncing) return;

    if (target.hasAttribute('data-bra-size')) {
      this.#handleSizeChange();
    } else {
      this.#handleComboChange();
    }

    // 所有用户交互的统一出口：Size 是否已确定决定加购按钮能不能点
    this.#updatePendingState();
  };

  /** Band / Cup 变化 —— 对应老项目的 onBandCupChange */
  #handleComboChange() {
    const band = this.#bandSelect?.value ?? '';
    const cup = this.#cupSelect?.value ?? '';
    this.#lastCombo = { band, cup };

    this.#hideUnavailableState();

    // 都空：恢复全部可选，清掉 Size 与提示
    if (!band && !cup) {
      this.#updateSizeOptionStates('', '');
      if (this.#sizeSelect) this.#sizeSelect.value = '';
      this.#hideHint();
      return;
    }

    // 只选了一维：置灰不可达的尺码，清空 Size 等用户补齐另一维
    if (!band || !cup) {
      this.#updateSizeOptionStates(band, cup);
      if (this.#sizeSelect) this.#sizeSelect.value = '';
      this.#hideHint();
      return;
    }

    // 两维都选
    this.#updateSizeOptionStates(band, cup);

    const radio = this.#findRadio(this.#lookup(band, cup));
    if (!radio) {
      this.#showUnavailableState(`${band}${cup}`);
      return;
    }

    if (this.#sizeSelect) this.#sizeSelect.value = radio.value;
    this.#selectRadio(radio);
    this.#showHint(radio.value);
  }

  /** 用户直接改 Size 下拉 —— 对应老项目的 onSizeDirectChange */
  #handleSizeChange() {
    const value = this.#sizeSelect?.value ?? '';

    // 一个尺码对应多个 Band+Cup 组合，无法反向唯一确定 → 清空而非回填。
    // 同时清掉记录，否则 morph 后 #restoreAfterMorph 会把刚清掉的值又填回来。
    this.#lastCombo = { band: '', cup: '' };
    this.#isSyncing = true;
    if (this.#bandSelect) this.#bandSelect.value = '';
    if (this.#cupSelect) this.#cupSelect.value = '';
    this.#isSyncing = false;

    this.#hideUnavailableState();
    this.#updateSizeOptionStates('', '');

    if (!value) {
      this.#hideHint();
      return;
    }

    const radio = this.#findRadio(value);
    if (radio) this.#selectRadio(radio);
    this.#showHint(value);
  }

  /**
   * 原生 radio 被改动时把 Size 下拉同步过去：
   * - event：fieldset 上冒泡到的 change 事件
   * 本组件自己的 select 事件已在 #handleOwnChange 里 stopPropagation，到不了这里，
   * 但仍保留 radio 类型判断作为防御。
   */
  #handleRadioChange = (event) => {
    if (event.target?.type !== 'radio') return;
    if (this.#isSyncing) return;
    this.#syncSizeFromRadio();
  };

  /** morph 之后把被服务端 HTML 冲掉的客户端状态整套恢复 */
  #restoreAfterMorph() {
    this.#stripSelectedAttributes();

    // 只在下拉当前为空时恢复，避免覆盖用户正在进行的操作
    const band = this.#bandSelect;
    const cup = this.#cupSelect;
    if (band && !band.value && this.#lastCombo.band) band.value = this.#lastCombo.band;
    if (cup && !cup.value && this.#lastCombo.cup) cup.value = this.#lastCombo.cup;

    this.#syncSizeFromRadio();
    this.#updateSizeOptionStates(this.#lastCombo.band, this.#lastCombo.cup);

    // 反向提示也要恢复：它所在的 <p> 被 morph 按服务端 HTML 重写成了空且 hidden。
    // 而「已确定尺码」这个状态必然伴随一次变体切换（也就是必然 morph），
    // 不恢复的话这条提示实际上永远看不到。
    const size = this.#sizeSelect?.value;
    if (size) this.#showHint(size);
    else this.#hideHint();

    // 加购按钮也要重新判定：切换颜色等操作会让整个 section 重渲染，
    // 服务端渲染出来的按钮是启用态，而此刻 Size 可能还是空的。
    this.#updatePendingState();
  }

  /**
   * 本商品的加购按钮：内联购买区（真实按钮，被 CSS 隐藏）+ 吸底栏（傀儡按钮）。
   *
   * ⚠️ 必须按 data-product-id 筛，不能只按容器范围找 ——
   * Frequently Bought Together 里每个推荐商品都有自己的 add-to-cart-component，
   * 不带 id 条件会把**别人家商品**的加购按钮也禁掉（实测踩过：一次命中 10 个按钮，
   * 其中 6 个是 FBT 商品的）。
   *
   * 反过来，有了 id 条件之后作用域就该放宽到 document，不要再套 .shopify-section：
   * Quick Add 弹窗是「抓商品页 HTML 再注入」的（见 variant-picker-styles.liquid 的 doc），
   * 本组件会随之出现在弹窗里，而弹窗可能被 portal 到 section 之外 —— 那时
   * closest('.shopify-section') 返回 null，pending 就静默失效、Quick Add 里能买到未确定的尺码。
   * Quick Add 的表单同样带 product-form-component[data-product-id]（quick-add.liquid:47），
   * 所以 document 范围 + id 筛选两种场景都覆盖得到。
   */
  get #addToCartButtons() {
    const productId = this.dataset.productId;
    if (!productId) return [];

    const forms = document.querySelectorAll(
      `product-form-component[data-product-id="${productId}"] button[name="add"]`
    );
    const sticky = document.querySelectorAll(
      `sticky-add-to-cart[data-product-id="${productId}"] [ref="addToCartButton"]`
    );
    return [...forms, ...sticky].filter((el) => el instanceof HTMLButtonElement);
  }

  /**
   * 按「Size 是否已确定」同步加购按钮的可用性 —— 对应老项目的 setPendingSelection。
   *
   * 为什么必须做：只选了 Band（或只选了 Cup）时 Size 会被清空，但原生 radio 仍勾着
   * 上一次的尺码、表单里的 variantId 也还是旧的。此时若加购按钮可点，用户会买到
   * 「残留的那个尺码」而不是他正在挑的（实测：选 Band 46 后按钮仍可点，加购得到 4 (S)）。
   *
   * 只禁按钮、不动 input[name="id"]（老项目会连它一起禁）：那个 input 是 Horizon 变体
   * 机制的核心状态，动它风险大于收益；禁掉唯一的 submit 按钮已经能阻止提交
   * （隐式提交也会因为首个 submit 按钮 disabled 而不触发）。
   */
  /**
   * 找到加购按钮里承载「主文案」的那个元素：
   * - button：加购按钮
   *
   * ⚠️ 两种按钮的结构不同，不能用一个固定层级的选择器：
   *   吸底栏：.add-to-cart-text__content > span「Add to cart」
   *                                     > span[ref=quantityDisplay]「( )」> span[ref=quantityNumber]「1」
   *   内联：  .add-to-cart-text__content > span > span「Add to cart」
   * 用 `.add-to-cart-text__content span span` 会在吸底栏上命中 quantityNumber，
   * 把**数量**替换成文案，视觉变成「Add to cart (Select size)」（踩过）。
   * 所以取第一个直接子元素（跳过数量那个兄弟），再钻到最深一层带文本的元素。
   */
  #addButtonTextNode(button) {
    const content = button.querySelector('.add-to-cart-text__content');
    let node = content?.firstElementChild;
    if (!node) return null;
    while (node.firstElementChild && node.firstElementChild.textContent.trim()) {
      node = node.firstElementChild;
    }
    return node;
  }

  #updatePendingState() {
    const pending = !this.#sizeSelect?.value;
    const selectSizeText = this.dataset.selectSize || 'Select size';

    this.#addToCartButtons.forEach((button) => {
      const textNode = this.#addButtonTextNode(button);

      if (pending) {
        // 首次进入 pending 时记下原文案，恢复时用得上
        if (textNode && button.dataset.braOriginalText == null) {
          button.dataset.braOriginalText = textNode.textContent ?? '';
        }
        button.disabled = true;
        if (textNode) textNode.textContent = selectSizeText;
        return;
      }

      // 退出 pending：只恢复我们改过的部分，不去覆盖 Horizon 自己的售罄等状态
      if (button.dataset.braOriginalText != null) {
        if (textNode) textNode.textContent = button.dataset.braOriginalText;
        delete button.dataset.braOriginalText;
        // 尺码选定不等于变体请求完成，不能提前恢复仍在等待响应的购买入口。
        button.disabled = Boolean(button.closest('[data-variant-unresolved]'));
      }
    });
  }

  /**
   * 剥掉本组件内所有 option 的 selected 属性。
   *
   * 为什么需要：Horizon 的 morph 对表单元素有特殊处理，会把 <option> 的 selected 属性
   * 同步成当前 live 选中态以保留表单状态 —— 于是每次变体切换后，我们三个下拉当前选中的
   * option（Band/Cup 通常是那个空占位）都会被盖上 selected。而 Horizon 的
   * selectedOptionsValues 用 `select option[selected]` 全局扫描并要求每个命中项都有
   * data-option-value-id，空占位没有这个属性 → 抛 'No option value ID found' → 此后
   * 所有变体切换全部失效（实测症状：第一次切换正常，之后点什么都没反应）。
   *
   * 注意这与 #handleOwnChange 的 stopPropagation 是两条**独立**的污染路径：
   * 前者拦事件冒泡，拦不住 morph 直接写 DOM。两处都要堵。
   *
   * 移除属性不影响显示 —— select 的当前值由 .value(property) 决定，与 attribute 无关。
   */
  #stripSelectedAttributes() {
    this.querySelectorAll('option[selected]').forEach((option) => {
      option.removeAttribute('selected');
    });
  }

  /**
   * 把 Size 下拉的显示值对齐当前勾选的 radio。
   * 用 .value 赋值（只改 property、不写 selected 属性）—— 这点是硬约束，
   * 写 attribute 会踩到文件头第 2 条那个坑。
   */
  #syncSizeFromRadio() {
    const checked = this.#sizeRadios.find((radio) => radio.checked);
    const select = this.#sizeSelect;
    if (checked && select && select.value !== checked.value) {
      select.value = checked.value;
    }
  }

  /**
   * 勾选 radio 并派发冒泡 change，交给 Horizon 的 variant-picker 接管：
   * - radio：目标 input 元素
   * #isSyncing 期间 #handleRadioChange 会跳过，避免自己触发自己。
   */
  #selectRadio(radio) {
    if (radio.checked) return;
    this.#isSyncing = true;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    this.#isSyncing = false;
  }

  /**
   * Band+Cup 都选了但查不到对应尺码：
   * - combo：用户选的组合，如 '38A'
   * 整个 Size 下拉隐藏、原位换成「无对应尺码」框，并显示红色 alert 文案（与老项目一致）。
   */
  #showUnavailableState(combo) {
    this.#isSyncing = true;
    if (this.#sizeSelect) this.#sizeSelect.value = '';
    this.#isSyncing = false;

    this.#sizeWrapper?.setAttribute('hidden', '');
    this.#sizeUnavailableEl?.removeAttribute('hidden');

    const error = this.#errorEl;
    if (error) {
      error.textContent = (error.dataset.template ?? '').replace('__COMBO__', combo);
      error.hidden = false;
    }

    this.#hideHint();
  }

  #hideUnavailableState() {
    this.#sizeWrapper?.removeAttribute('hidden');
    this.#sizeUnavailableEl?.setAttribute('hidden', '');

    const error = this.#errorEl;
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
  }

  /**
   * 显示反向提示「Size 6 (M) fits 36B, 38A…」：
   * - sizeValue：已确定的尺码，如 '6 (M)'
   * 查不到对应组合时隐藏（与老项目一致）。
   */
  #showHint(sizeValue) {
    const hint = this.#hintEl;
    if (!hint) return;

    const fits = (this.#fitLabels[sizeValue] ?? []).join(', ');
    if (!fits) {
      this.#hideHint();
      return;
    }

    hint.textContent = (hint.dataset.template ?? '')
      .replace('__SIZE__', sizeValue)
      .replace('__FITS__', fits);
    hint.hidden = false;
  }

  #hideHint() {
    const hint = this.#hintEl;
    if (!hint) return;
    hint.textContent = '';
    hint.hidden = true;
  }
}

if (!customElements.get('bra-size-mapper')) {
  customElements.define('bra-size-mapper', BraSizeMapper);
}
